import { Theme, ThemeProvider, useTheme } from '@aws-amplify/ui-react';
import './App.scss';
import { Navigation } from './components/nav/Navigation';
import stack from './aws-exports.json';

import '@aws-amplify/ui-react/styles.css';

import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Auth, Hub } from 'aws-amplify';
import { useEffect, useRef, useState } from 'react';
import { Card, Col, Container } from 'react-bootstrap';
import AssistantPanel from './components/assistant/AssistantPanel';
import { GlobalOptionsProvider } from './components/common/GlobalOptionsContext';
import './fonts/DM_Sans/DMSans-VariableFont_opsz,wght.ttf';
import './fonts/Nunito/Nunito-VariableFont_wght.ttf';
import SignIn from './SignIn';
import { DashboardTriggerProvider } from './context/DashboardTriggerContext';
import { BrowserRouter } from 'react-router-dom';
import { getUserEmail } from './utils/user';
import { callFhirActiveCsnByPatientFhirID } from './amplify/api-gateway/fetch-sync-fhir';
import { waitForUmami } from './amplify/api-gateway/user-events';
import { SystemBanner } from './components/common/SystemBanner';


const App = (props: {
    tenant: any,
    ws: string,
    epicClientID: string,
}) => {
    const { tokens } = useTheme();

    const theme: Theme = {
        name: 'PhysioHub Theme',
        tokens: {
            colors: {
                brand: {
                    primary: {
                        '10': tokens.colors.red['10'],
                        '80': tokens.colors.red['80'],
                        '90': tokens.colors.red['90'],
                        '100': tokens.colors.red['100'],
                    },
                },
            },
        },
    };

    // List of excluded path prefixes
    const excludedPrefixes = ['/llm-visit/revisit/', '/llm-visit/admit/'];

    // Check if the current path starts with any excluded prefix
    const isExcludedPath = excludedPrefixes.some(prefix => window.location.pathname.startsWith(prefix));

    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [assistantOpen, setAssistantOpen] = useState(!isExcludedPath);
    const [assistantWidth, setAssistantWidth] = useState(400);

    const userRef = useRef(user);
    const [unauthorized, setUnauthorized] = useState<string | null>(null);

    const CHECK_INTERVAL_MS = 60 * 1000; // check every 1 minute

    function smartAuth(epicRedirectCode?: string) {
        console.log("smartAuth triggered");

        const getParamFromUrl = (param: string) => {
            const queryString = window.location.search;
            const queryParams = new URLSearchParams(queryString);
            return queryParams.get(param);
        };

        const redirectToAuthorizationServer = (authorizationUri: string) => {
            const redirectUri = window.location.origin;
            const audUri = baseUrl!;
            const state = ""; // Additional custom state that can be used by this app

            // Create a form and submit it as POST
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = authorizationUri;
            
            // Create form fields
            const fields = {
                scope: 'launch openid fhirUser launch/patient launch/encounter',
                response_type: 'code',
                redirect_uri: redirectUri,
                client_id: props.epicClientID,
                launch: launchCode,
                state: state,
                aud: audUri
            };

            // Add each field to the form
            Object.entries(fields).forEach(([key, value]) => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = value || '';
                form.appendChild(input);
            });

            // Add form to document and submit
            document.body.appendChild(form);
            form.submit();
        };

        const exchangeAuthorizationCodeForAccessToken = async (tokenUri: string, code: string) => {
            const redirectUri = window.location.origin;

            // Get OIDC Bridge URL from configuration
            const oidcBridgeUrl = stack['EDPlatformOIDCBridge'];

            if (!oidcBridgeUrl) {
                throw new Error("OIDC Bridge URL not configured");
            }

            // Call the proxy endpoint instead of direct FHIR server
            const proxyUrl = `${oidcBridgeUrl}/exchange-token`;

            try {
                const response = await fetch(proxyUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        tokenUri,
                        code,
                        clientId: props.epicClientID,
                        redirectUri
                    }),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(`Token exchange failed: ${errorData.message || response.statusText}`);
                }

                const json = await response.json();
                return json;
            } catch (error) {
                console.error("Error exchanging token via proxy:", error);
                throw error;
            }
        };

        const getSmartConfiguration = async (returnUriType: string) => {
            // Get SMART configuration via proxy endpoint to bypass private network CORS error
            const baseUrl = sessionStorage.getItem("iss");

            if (!baseUrl) {
                throw new Error("No ISS found in session storage");
            }

            // Get OIDC Bridge URL from configuration
            const oidcBridgeUrl = stack['EDPlatformOIDCBridge'];

            if (!oidcBridgeUrl) {
                throw new Error("OIDC Bridge URL not configured");
            }

            // Call the proxy endpoint instead of direct FHIR server
            const proxyUrl = `${oidcBridgeUrl}/smart-config?iss=${encodeURIComponent(baseUrl)}`;

            try {
                const response = await fetch(proxyUrl, {
                    method: "GET",
                    headers: {
                        "Accept": "application/json",
                    },
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(`SMART config fetch failed: ${errorData.message || response.statusText}`);
                }

                const config = await response.json();

                // Return the appropriate endpoint based on request type
                if (returnUriType === "authorize") {
                    return config.authorization_endpoint;
                } else {
                    return config.token_endpoint;
                }
            } catch (error) {
                console.error("Error fetching SMART configuration via proxy:", error);
                throw error;
            }
        };

        const baseUrl = getParamFromUrl("iss");
        if (baseUrl) {
            sessionStorage.setItem("iss", baseUrl);
        }

        const launchCode = getParamFromUrl("launch");
        const state = getParamFromUrl("state");

        console.log("window.location.search", window.location.search, "epicRedirectCode", epicRedirectCode);

        if (baseUrl && launchCode) {
            // Initial launch from the EHR
            getSmartConfiguration("authorize").then((output: any) => {
                const authorizationUri = output;
                // Redirect to Epic for authorization using POST
                redirectToAuthorizationServer(authorizationUri);
            }).catch((err: any) => {
                console.error("Error fetching SMART configuration", err);
            });
        } else if (epicRedirectCode) {
            console.log("found redirect with code", epicRedirectCode);
            // EHR authorization redirections back
            getSmartConfiguration("token").then((output: any) => {
                const tokenUri = output; // Fixed variable name - was authorizationUri
                // Exchange code for access token
                exchangeAuthorizationCodeForAccessToken(tokenUri, epicRedirectCode).then((json: any) => {
                    const patientId = json.patient;
                    console.log("success", json);

                    callFhirActiveCsnByPatientFhirID({
                        fhir_id: patientId,
                    }).then((csn) => {
                        if (csn) {
                            console.log("Found active CSN", csn);
                            
                            // Construct the final redirect URL
                            const redirectUrl = `${window.location.origin}/trackboard/${csn}`;

                            // Redirect the user
                            window.location.href = redirectUrl;
                        } else {
                            console.log("Did not find an active CSN");
                            window.location.href = window.location.origin;
                        }
                    });
                }).catch((err: any) => {
                    console.error("Error fetching access token", err);
                });
            }).catch((err: any) => {
                console.error("Error fetching SMART configuration", err);
            });
        } else {
            // Invalid EHR redirect
            console.log("Invalid EHR redirect");
        }
    }

    function useAuthSessionMonitor() {
        useEffect(() => {
            const interval = setInterval(async () => {
                try {
                    // Check if user is signed in
                    await Auth.currentAuthenticatedUser(); // throws if not signed in

                    // Auth.currentSession() automatically refreshes tokens if they're expired
                    // and the refresh token is still valid. We just need to call it.
                    // If the refresh token is expired, it will throw an error.
                    await Auth.currentSession();

                    // If we get here, session is valid (either still active or was auto-refreshed)
                    console.debug('Session check passed - tokens are valid or were refreshed');
                } catch (err) {
                    // Session is invalid and cannot be refreshed (refresh token expired or other error)
                    console.warn('Session cannot be refreshed. Signing out...', err);
                    if (userRef.current) {
                        await Auth.signOut();
                        window.location.reload();
                    } else {
                        console.info("Skipping reload because user is not present");
                    }
                }
            }, CHECK_INTERVAL_MS);

            return () => clearInterval(interval);
        }, []);
    }
    useAuthSessionMonitor();

    // Inactivity timeout for SOC2 compliance
    // Logs user out after 15 minutes of inactivity across ALL tabs
    function useInactivityTimeout() {
        const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
        const ACTIVITY_STORAGE_KEY = 'lastUserActivity';
        const ACTIVITY_BROADCAST_THROTTLE_MS = 5000; // Only broadcast to other tabs every 5 seconds
        const lastActivityRef = useRef(Date.now());
        const lastBroadcastRef = useRef(Date.now());

        useEffect(() => {
            // Only track activity if user is logged in
            if (!user) return;

            const updateActivity = () => {
                const now = Date.now();
                lastActivityRef.current = now;

                // Throttle localStorage writes - only broadcast every 5 seconds
                // This prevents performance issues from frequent localStorage writes
                if (now - lastBroadcastRef.current >= ACTIVITY_BROADCAST_THROTTLE_MS) {
                    localStorage.setItem(ACTIVITY_STORAGE_KEY, now.toString());
                    lastBroadcastRef.current = now;
                }
            };

            // Listen for activity from other tabs
            const handleStorageChange = (e: StorageEvent) => {
                if (e.key === ACTIVITY_STORAGE_KEY && e.newValue) {
                    const timestamp = parseInt(e.newValue, 10);
                    if (!isNaN(timestamp)) {
                        lastActivityRef.current = timestamp;
                        console.debug('Activity detected in another tab');
                    }
                }
            };

            // Initialize with current timestamp in localStorage
            localStorage.setItem(ACTIVITY_STORAGE_KEY, Date.now().toString());

            // Track user activity events
            const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
            activityEvents.forEach(event => {
                document.addEventListener(event, updateActivity, true);
            });

            // Listen for storage events from other tabs
            window.addEventListener('storage', handleStorageChange);

            // Check for inactivity every minute
            const inactivityCheck = setInterval(async () => {
                const inactiveTime = Date.now() - lastActivityRef.current;

                if (inactiveTime >= INACTIVITY_TIMEOUT_MS) {
                    console.warn('User inactive for 15 minutes across all tabs. Signing out...');
                    localStorage.setItem('signOutReason', 'You were signed out due to inactivity.');
                    try {
                        await Auth.signOut();
                        window.location.reload();
                    } catch (error) {
                        console.error('Error signing out due to inactivity', error);
                    }
                }
            }, 60000); // Check every minute

            return () => {
                activityEvents.forEach(event => {
                    document.removeEventListener(event, updateActivity, true);
                });
                window.removeEventListener('storage', handleStorageChange);
                clearInterval(inactivityCheck);
            };
        }, [user]);
    }
    useInactivityTimeout();

    useEffect(() => {
        userRef.current = user;

        if (!user) return;

        const email = getUserEmail(user);
        if (!email) {
            console.warn("UMAMI - No email found for user:", user);
            return;
        }

        let cancelled = false;

        const initializeUmami = async () => {
            try {
                await waitForUmami();

                if (!cancelled) {
                    console.log("UMAMI - Identifying and tracking user:", email);
                    // In the umami dashboard, you will see all user events tied to
                    // this email. However, events prior to this page (e.g. login page) 
                    // will appear as its own session
                    window.umami.identify(email);
                    // window.umami.track(`user_change?user=${email}`);
                }
            } catch (error) {
                if (!cancelled) {
                    console.error("UMAMI - Failed to initialize:", error);
                }
            }
        };

        initializeUmami();

        return () => {
            cancelled = true;
        };
    }, [user]);

    useEffect(() => {
        setUnauthorized(null);

        const urlParams = new URLSearchParams(window.location.search);
        const hasLaunchParams = urlParams.has('launch') && urlParams.has('iss');
        const hasCodeParams = urlParams.has('code');
        const hasProcessedParams = urlParams.has('smart_launch') && urlParams.has('smart_iss');
        
        if (hasCodeParams) {
            // console.log("SMART launch code detected");
            const epicRedirectCode = urlParams.get("code") || undefined;

            // Check if user is already authenticated before making the call
            Auth.currentAuthenticatedUser()
                .then(() => {
                    // User is authenticated, make the updateinfo call
                    smartAuth(epicRedirectCode);
                })
                .catch(() => {
                    // User not authenticated, trigger full OIDC flow
                    console.log('Unknown state with code params');
                });
        } else if (hasLaunchParams && !hasProcessedParams) {
            // console.log("SMART launch detected - params not processed yet");

            // Fresh SMART launch - rename params and trigger OIDC
            const launch = urlParams.get('launch');
            const iss = urlParams.get('iss');
            // console.log(`SMART launch | launch: ${launch} and iss: ${iss}`);

            // Check if user is already authenticated before making the call
            Auth.currentAuthenticatedUser()
                .then(() => {
                    // User is authenticated, make the updateinfo call
                    smartAuth();
                })
                .catch(() => {
                    // User not authenticated, trigger full OIDC flow
                    console.log('User not authenticated, triggering OIDC flow');

                    urlParams.set('smart_launch', urlParams.get('launch')!);
                    urlParams.set('smart_iss', urlParams.get('iss')!);
                    urlParams.delete('launch');
                    urlParams.delete('iss');
                    const modifiedUrl = `${window.location.origin}${window.location.pathname}?${urlParams.toString()}`;

                    Auth.federatedSignIn({
                        customProvider: 'smart-oidc',
                        customState: modifiedUrl,
                    });
                });
        } else {
            // Check if the user is already authenticated
            Auth.currentAuthenticatedUser()
                .then((u: any) => {
                    // console.log("CURRENT AUTH USER", u);
                    Auth.currentSession().then((session: any) => {
                        const idToken = session.getIdToken().getJwtToken();
                        // console.log("idToken:", idToken);
                        const idTokenPayload = session.getIdToken().decodePayload();
                        // console.log("Decoded idToken:", session.getIdToken().decodePayload());
                        const csn = idTokenPayload["custom:csn"];
                        // console.log("Found CSN", csn);
                        // console.log("hasProcessedParams", hasProcessedParams);
                        const path = window.location.pathname.replace(/\/+$/, ''); // remove trailing slashes
                        if (csn && hasProcessedParams && (path.startsWith('/trackboard') || path.startsWith('/dashboard'))) {
                            // Strip the processed params (as it's a redirect back from OAuth)
                            // and do a final redirect to the actual CSN location
                            // Note: We now handle any /dashboard/* path to catch malformed URLs

                            // Remove the processed SMART launch params
                            urlParams.delete('smart_launch');
                            urlParams.delete('smart_iss');

                            // Construct the final redirect URL
                            const redirectUrl = `${window.location.origin}/trackboard/${csn}?${urlParams.toString()}`;

                            // Redirect the user
                            window.location.href = redirectUrl;
                        }
                    });

                    setIsLoading(false);
                    setUser(u);
                })
                .catch((e: Error) => {
                    console.error("User not signed in", e.toString());
                    setIsLoading(false);
                });
        }

        Hub.listen('auth', (data) => {
            const { payload } = data;
            // console.log('New auth payload: ', payload);
            if (payload.event === "customOAuthState") {
                // User is redirected back from SSO login
                if (payload.data && payload.data.startsWith("http")) {
                    window.location.href = payload.data;
                }
            }
            // console.log('New auth event: ', data);
        });
    }, []);

    const handleSignIn = async (username: string, password: string) => {
        try {
            const user = await Auth.signIn(username, password);
            setUser(user);
            setUnauthorized(null);
        } catch (error) {
            console.error("Error signing in:", error);
            let errorMessage = `${error}`;
            errorMessage = errorMessage.split(": ").length > 1 ? errorMessage.split(": ")[1] : errorMessage;
            setUnauthorized(errorMessage);
        }
    };

    const handleFederatedSignIn = (provider: string) => {
        Auth.federatedSignIn({customProvider: provider, customState: window.location.href}); // Redirect to the SAML SSO login page
    };

    const handleSignOut = async () => {
        try {
            // await Auth.signOut();
            await Auth.signOut({ global: false });
            setUser(null);
        } catch (error) {
            console.error("Error signing out:", error);
        }
    };

    if (isLoading) {
        return (
            <Container fluid>
                <Col className='d-flex align-items-center justify-content-center'>
                    <Card style={{marginTop: 50, marginBottom: 50, width: '600px'}} className='p-5'>
                        <h6><FontAwesomeIcon icon={faSpinner} spin /> Loading...</h6>
                    </Card>
                </Col>
            </Container>
        )
    }

    if (!user) {
        // Display a sign-in button for SAML
        return (
            <SignIn unauthorizedReason={unauthorized} handleSignIn={handleSignIn} handleFederatedSignIn={handleFederatedSignIn} tenant={props.tenant} />
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <div className="App">
                <GlobalOptionsProvider>
                    <DashboardTriggerProvider>
                        {/* Layout Wrapper */}
                        <div 
                            className={`main-layout ${assistantOpen ? 'assistant-layout-shifted' : ''}`}
                            style={{
                                marginRight: assistantOpen ? `${assistantWidth}px` : undefined
                            }}
                        >
                            <BrowserRouter>
                                <Navigation
                                    tenant={props.tenant}
                                    ws={props.ws}
                                    user={user}
                                    assistantOpen={assistantOpen}
                                    setAssistantOpen={setAssistantOpen}
                                    signOut={handleSignOut}
                                    assistantWidth={assistantWidth}
                                />
                            </BrowserRouter>

                            {/* Assistant Panel */}
                            <AssistantPanel 
                                isOpen={assistantOpen}
                                setIsOpen={setAssistantOpen}
                                ws={props.ws}
                                user={user}
                                tenant={props.tenant}
                                width={assistantWidth}
                                setWidth={setAssistantWidth}
                            />

                            <SystemBanner />
                        </div>
                    </DashboardTriggerProvider>
                </GlobalOptionsProvider>
            </div>
        </ThemeProvider>
    );

    // return (
    //     <ThemeProvider theme={theme}>
    //         <Authenticator components={components} hideSignUp={true}>
    //             {({ signOut, user }) => (
    //                 <div className="App">
    //                     <Navigation
    //                         tenant={props.tenant}
    //                         ws={props.ws}
    //                         user={user}
    //                         signOut={signOut}
    //                     />
    //                 </div>
    //             )}
    //         </Authenticator>
    //     </ThemeProvider>
    // );
}

export default App;
